import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginUser } from '../../services/accountService';
import { useAuth } from '../../context/AuthContext';
import './Login.css';
import logo from '../../assets/icon.png';
import Breadcrumbs from '../../components/Breadcrumbs';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import InviteFormModal from '../../components/InviteFormModal/InviteFormModal';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    if (window.prerenderReady !== undefined) {
      window.prerenderReady = true;
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const result = await loginUser({ email, password }, navigate);

      if (result.success) {
        const Cookies = (await import('js-cookie')).default;
        const token = Cookies.get('jwtToken');
        const userId = Cookies.get('userId');
        const subscriptionType = Cookies.get('subscriptionType');
        if (token) {
          await login(token, userId, subscriptionType);
        }
        // Decode roles directly from the JWT so the redirect doesn't race
        // the AuthContext effect that populates user.roles.
        let roles = [];
        try {
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const claim =
              payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
              payload['role'] || [];
            roles = Array.isArray(claim) ? claim : [claim];
          }
        } catch { /* fall through to default redirect */ }
        const isAdmin = roles.includes('Admin');
        const isCRM = roles.includes('CRM');
        if (!isAdmin && isCRM) {
          // CRM-only users land directly on the lead pipeline (the surface
          // they're meant to operate). Admins keep the default home.
          navigate('/admin/leads');
        } else {
          navigate('/');
        }
      } else {
        setError(result.error || 'Credentiale invalide.');
      }
    } catch (err) {
      setError(err.message || 'A aparut o eroare.');
    } finally {
      setIsLoading(false);
    }
  };

  const breadcrumbLinks = [{ href: '/', label: 'Home' }, { label: 'Login' }];

  const pageTitle = `Autentificare - ${import.meta.env.VITE_NAME} | Portal Achiziții Publice`;
  const pageDescription = `Autentifică-te pe ${import.meta.env.VITE_NAME} pentru a accesa cele mai recente licitații publice și resurse dedicate achizițiilor publice din România.`;
  const pageKeywords = `autentificare, login, achiziții publice, licitații publice, Licitatii-Constructii, acces platformă, autentificare utilizator`;
  const pageUrl = `${window.location.origin}/login`;

  return (
    <>
      <SeoPageComponent
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        pageKeywords={pageKeywords}
        pageUrl={pageUrl}
        logoUrl={logo}
      />
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-green-100 py-12">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-10 border-t-8 border-indigo-500 animate-fade-in">
          <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />
          <h2 className="text-3xl md:text-4xl font-extrabold text-indigo-700 mb-2 text-center">
            Bine ai revenit!
          </h2>
          <p className="text-gray-500 text-center mb-8 text-lg">
            Autentifică-te și accesează rapid toate oportunitățile din
            construcții.
          </p>
          {error && (
            <p className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4 text-center">
              {error}
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Adresă de email"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                required
              />
            </div>
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Parolă"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                required
              />
            </div>
            <div className="flex justify-end">
              <a
                href="/forgot-password"
                className="text-indigo-600 font-semibold hover:underline text-sm"
              >
                Am uitat parola?
              </a>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-indigo-500 to-green-500 hover:from-indigo-600 hover:to-green-600 disabled:from-gray-400 disabled:to-gray-400 text-white font-bold py-3 rounded-xl shadow-lg text-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Se autentifica...
                </>
              ) : (
                'Autentificare'
              )}
            </button>
          </form>
          <div className="flex items-center my-6">
            <span className="flex-1 h-px bg-gray-200"></span>
            <span className="mx-4 text-gray-400 font-semibold">sau</span>
            <span className="flex-1 h-px bg-gray-200"></span>
          </div>
          <div className="text-center">
            <p className="text-gray-600">
              Nu ai un cont?{' '}
              <button
                type="button"
                onClick={() => setShowInviteModal(true)}
                className="text-indigo-600 font-bold hover:underline bg-transparent border-0 p-0 cursor-pointer"
              >
                Solicită invitație
              </button>
            </p>
          </div>
        </div>
      </section>

      <InviteFormModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />
    </>
  );
};

export default Login;
